import { memo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Download,
  Settings,
  Trash2,
  Plus,
  Save,
  Upload,
  FileJson,
  X,
  Activity,
  Loader2,
  Check,
  FolderOpen,
  Image,
  PlayCircle,
  StopCircle,
  Film,
  Shield,
  HelpCircle,
  Share2,
  ChevronDown,
  LayoutGrid,
  Clapperboard,
  MonitorPlay,
  Compass,
  Keyboard,
  AlertTriangle,
  Lightbulb,
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useWorkflowStore, isRunnableFlow } from '../../store/workflowStore';
import { useCreditsStore } from '../../store/creditsStore';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useActiveExecutions } from '../../hooks/useActiveExecutions';
import { useExecutionNotifications } from '../../hooks/useExecutionNotifications';
import useTimelineStore from '../../store/timelineStore';
import {
  EVT_DATA as LEARNING_DATA_EVENT, hasUnseen, openFeedback, openLearning,
} from '../learning/learningModel';
import { isSoundEnabled, setSoundEnabled } from '../../utils/soundPin';
import { toast } from 'sonner';
import ExecutionHistory from './ExecutionHistory';
import CreditsBadge from './CreditsBadge';
import CreditsPanel from './CreditsPanel';
import LowBalanceBanner from './LowBalanceBanner';
import ProjectsPanel from './ProjectsPanel';
import BuyCreditsModal from './BuyCreditsModal';
import GalleryPanel from './GalleryPanel';
import LibraryWindow from './LibraryWindow';
import ProjectPersonalization from './ProjectPersonalization';
import HelpAgentWidget from './HelpAgentWidget';
import CanvasBackgroundSelector from './CanvasBackgroundSelector';
import AdminPanel from '../admin/AdminPanel';
import McpConnectionsPanel from './McpConnectionsPanel';
import { mcpService } from '../../services/mcpService';
import { Plug } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useRevealStore } from '../../store/revealStore';
import { useLmxStore } from '../../lmx/lmxStore';
import { UserProfile } from '../auth';
import UserMenu from './UserMenu';
// Envío UI-2 · barra única (layout nuevo, detrás de flag)
import { LAYOUT_V2 } from '../../layout/layoutV2';

const clerkEnabled = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const TimelineToggleButton = () => {
  const isOpen = useTimelineStore((s) => s.isOpen);
  const toggle = useTimelineStore((s) => s.toggleEditor);
  return (
    <button
      className={`nav-projects-btn tl-topnav-trigger ${isOpen ? 'active' : ''}`}
      onClick={toggle}
      title="Abrir Timeline Editor"
      data-testid="timeline-toggle-btn"
    >
      <Film size={15} />
      Timeline
    </button>
  );
};

const TopNavBar = memo(() => {
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  const [showCreditsPanel, setShowCreditsPanel] = useState(false);
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  // Envío PJ-2 · Biblioteca de marcas y personajes: null | {tab}
  const [library, setLibrary] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showMcpPanel, setShowMcpPanel] = useState(false);
  const [mcpAccess, setMcpAccess] = useState(false);
  // Glow discreto en "Ejecutar flujo completo" al terminar la animación de
  // aparición del workflow. No cambia el comportamiento del botón.
  const spotlightRun = useRevealStore((s) => s.spotlightRun);
  const [showHelpAgent, setShowHelpAgent] = useState(false);
  // E6f · menú "?" (recorrido / demo / atajos) — sólo si LMX está disponible.
  const [showTourMenu, setShowTourMenu] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const lmxAvailable = useLmxStore((s) => s.available);
  // E6f · REACT_APP_LMX_TOUR=false apaga el recorrido entero (también este menú).
  const tourEnabled = lmxAvailable
    && String(process.env.REACT_APP_LMX_TOUR ?? 'true') !== 'false';
  const openTour = useCallback((startAt) => {
    window.dispatchEvent(new CustomEvent('lmx:tour', { detail: { startAt } }));
  }, []);
  // Envío AP-1 · ¿hay capítulos de aprendizaje publicados sin ver? (punto en «?»)
  const [learningData, setLearningData] = useState(null);
  useEffect(() => {
    const onData = (e) => setLearningData(e.detail || null);
    window.addEventListener(LEARNING_DATA_EVENT, onData);
    return () => window.removeEventListener(LEARNING_DATA_EVENT, onData);
  }, []);
  const learningAvailable = (learningData?.chapters || []).length > 0;
  const learningUnseen = hasUnseen(learningData);

  // Envío LMX-5 · los botones de la guía de LMX abren cada parte de la
  // plataforma (evento `sml:open` con un destino de lmx/knowledge.py).
  const navigate = useNavigate();
  const openTargetRef = useRef(null);
  openTargetRef.current = (target, detail = {}) => {
    const tl = useTimelineStore.getState();
    const panel = (name) => {
      if (!tl.isOpen) tl.openEditor();
      setTimeout(() => window.dispatchEvent(
        new CustomEvent('sml:timeline-panel', { detail: { panel: name } })), 250);
    };
    switch (target) {
      case 'canvas': if (tl.isOpen) tl.closeEditor(); break;
      case 'edicion': if (!tl.isOpen) tl.openEditor(); break;
      case 'marca': panel('marca'); break;
      case 'personajes': panel('personajes'); break;
      case 'plantillas': panel('plantillas'); break;
      case 'redes': navigate('/settings?tab=social'); break;
      case 'mcp':
        if (mcpAccess) setShowMcpPanel(true);
        else toast.info('El acceso MCP todavía no está habilitado para tu usuario: lo activa un administrador.', { position: 'bottom-right' });
        break;
      case 'galeria': setShowGallery(true); break;
      case 'biblioteca': setLibrary({ tab: detail.tab === 'marcas' ? 'marcas' : 'personajes' }); break;
      case 'creditos': setShowCreditsPanel(true); break;
      case 'proyectos': setShowProjectsPanel(true); break;
      case 'tour': openTour(); break;
      case 'demo': openTour('demo'); break;
      case 'videos': openLearning(detail.chapterId); break;
      case 'reportar': openFeedback('problem'); break;
      case 'sugerir': openFeedback('idea'); break;
      default: break;
    }
  };
  useEffect(() => {
    const onOpen = (e) => openTargetRef.current?.(e.detail?.target, e.detail || {});
    window.addEventListener('sml:open', onOpen);
    return () => window.removeEventListener('sml:open', onOpen);
  }, []);

  // Cerrar el menú "?" al tocar afuera.
  useEffect(() => {
    if (!showTourMenu) return undefined;
    const close = (e) => {
      if (!e.target.closest('.tour-menu-wrap')) setShowTourMenu(false);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [showTourMenu]);
  // Envío UI-2 · menús de la barra única: 'project' | 'help' | 'settings' | null
  const [v2Menu, setV2Menu] = useState(null);
  const timelineOpen = useTimelineStore((s) => s.isOpen);
  useEffect(() => {
    if (!v2Menu) return undefined;
    const close = (e) => { if (!e.target.closest('.v2-menu-wrap')) setV2Menu(null); };
    const esc = (e) => { if (e.key === 'Escape') setV2Menu(null); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [v2Menu]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  // Envío LMX-4 (I-30): el mismo modal sirve para «Guardar como» y para
  // «Nuevo proyecto» (que ahora pide nombre y crea el proyecto en el acto).
  const [saveMode, setSaveMode] = useState('save');
  const [projectNameInput, setProjectNameInput] = useState('');
  const [projectDescInput, setProjectDescInput] = useState('');

  const showBuyModal = useCreditsStore(s => s.showBuyModal);
  const closeBuyModal = useCreditsStore(s => s.closeBuyModal);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const nodes = useWorkflowStore(s => s.nodes);
  // UI-3 · aviso en la pestaña Canvas: nodos que aparecieron mientras estabas
  // en Edición (por ejemplo, los que agrega LMX).
  const nodesAtEditRef = useRef(null);
  const [newNodesWhileEditing, setNewNodesWhileEditing] = useState(0);
  const timelineIsOpen = useTimelineStore((s) => s.isOpen);
  useEffect(() => {
    if (!LAYOUT_V2) return;
    if (!timelineIsOpen) { nodesAtEditRef.current = null; setNewNodesWhileEditing(0); return; }
    if (nodesAtEditRef.current == null) nodesAtEditRef.current = new Set(nodes.map((n) => n.id));
    setNewNodesWhileEditing(nodes.filter((n) => !nodesAtEditRef.current.has(n.id)).length);
  }, [nodes, timelineIsOpen]);
  const clearCanvas = useWorkflowStore(s => s.clearCanvas);
  const showFooter = useWorkflowStore(s => s.showFooter);
  const showBadge = useWorkflowStore(s => s.showBadge);
  const toggleFooter = useWorkflowStore(s => s.toggleFooter);
  const toggleBadge = useWorkflowStore(s => s.toggleBadge);
  const projectName = useWorkflowStore(s => s.projectName);
  const loadSavedProjects = useWorkflowStore(s => s.loadSavedProjects);
  const saveProject = useWorkflowStore(s => s.saveProject);
  const saveProjectAsNew = useWorkflowStore(s => s.saveProjectAsNew);
  const newProject = useWorkflowStore(s => s.newProject);
  const exportProject = useWorkflowStore(s => s.exportProject);
  const importProject = useWorkflowStore(s => s.importProject);
  const currentProjectId = useWorkflowStore(s => s.currentProjectId);
  const hasUnsavedChanges = useWorkflowStore(s => s.hasUnsavedChanges);
  const isSavingProject = useWorkflowStore(s => s.isSavingProject);
  const initializeLastProject = useWorkflowStore(s => s.initializeLastProject);
  const isRunningWorkflow = useWorkflowStore(s => s.isRunningWorkflow);
  // Envío LMX-4 (I-31): «Ejecutar flujo» sólo cuando hay un flujo ejecutable
  // (mismas condiciones que startWorkflow) o uno corriendo.
  const showRunButton = isRunningWorkflow || isRunnableFlow(nodes);
  const workflowQueue = useWorkflowStore(s => s.workflowQueue);
  const workflowCurrentIndex = useWorkflowStore(s => s.workflowCurrentIndex);
  const workflowError = useWorkflowStore(s => s.workflowError);
  const startWorkflow = useWorkflowStore(s => s.startWorkflow);
  // E6c.5 · cuántas generaciones hay en curso en el canvas (de cualquier nodo).
  const inFlight = useWorkflowStore(s => s.inFlight);
  const inFlightCount = Object.values(inFlight || {})
    .reduce((acc, list) => acc + list.length, 0);
  const cancelWorkflow = useWorkflowStore(s => s.cancelWorkflow);

  // Enable auto-save + beforeunload warning
  useAutoSave();

  // Detect admin role from Clerk public_metadata.role.
  // Server still re-validates on every /api/admin/* call.
  const { user: clerkUser } = useUser();
  const isAdmin = clerkUser?.publicMetadata?.role === 'admin';

  // Live polling of active (queued + processing) executions for the current user
  const { active: activeExecList, count: activeCount, processing: processingList, queued: queuedList } = useActiveExecutions();

  // Global notifications: toast on queued→processing + toast & pin sound on completed/failed
  useExecutionNotifications(activeExecList);

  // Sound on/off preference (persisted in localStorage via soundPin util)
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());
  const handleToggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      setSoundEnabled(next);
      return next;
    });
  }, []);

  // Workflow completion/error notifications
  const prevRunningRef = useRef(isRunningWorkflow);
  useEffect(() => {
    if (prevRunningRef.current && !isRunningWorkflow) {
      if (workflowError) {
        toast.error(`Flujo detenido: ${workflowError}`, { duration: 4000, position: 'bottom-right' });
      } else {
        toast.success('Flujo completado exitosamente', { duration: 3000, position: 'bottom-right' });
      }
    }
    prevRunningRef.current = isRunningWorkflow;
  }, [isRunningWorkflow, workflowError]);

  // Initialize projects: ensure default exists, load list, restore last project
  // El puntero "último proyecto" se aísla por usuario: sin eso, entrar por la
  // vista previa (o con otra cuenta en el mismo navegador) traía flujos viejos.
  useEffect(() => {
    if (!clerkUser?.id) return;
    initializeLastProject(clerkUser.id);
  }, [clerkUser?.id, initializeLastProject]);

  // Sin Clerk (vista previa anónima) también hay que inicializar, pero con un
  // puntero propio para no heredar el proyecto de una sesión con cuenta.
  const clerkUserIdRef = useRef(clerkUser?.id || null);
  clerkUserIdRef.current = clerkUser?.id || null;
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!clerkUserIdRef.current) initializeLastProject('anon-preview');
    }, 1500);
    return () => clearTimeout(timer);
  }, [initializeLastProject]);

  // MCP: el botón aparece sólo si la plataforma lo tiene prendido Y este
  // usuario está habilitado (ambas cosas viven en la DB, no en el .env).
  useEffect(() => {
    let cancel = false;
    mcpService.getStatus()
      .then((s) => { if (!cancel) setMcpAccess(!!s.user_enabled); })
      .catch(() => { if (!cancel) setMcpAccess(false); });
    return () => { cancel = true; };
  }, []);


  const handleSaveClick = useCallback(async () => {
    if (currentProjectId) {
      try {
        await saveProject();
        setSaveSuccess(true);
        toast.success(`"${projectName}" guardado`, { duration: 2000, position: 'bottom-right' });
        setTimeout(() => setSaveSuccess(false), 2000);
      } catch (error) {
        toast.error('Error al guardar: ' + error.message, { position: 'bottom-right' });
      }
    } else {
      setSaveMode('save');
      setProjectNameInput(projectName);
      setProjectDescInput('');
      setShowSaveModal(true);
    }
  }, [currentProjectId, saveProject, projectName]);

  // UI-3 · Ctrl+S / Cmd+S guarda el proyecto (antes sólo lo anunciaba el botón).
  const saveShortcutRef = useRef(null);
  saveShortcutRef.current = handleSaveClick;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || String(e.key).toLowerCase() !== 's') return;
      e.preventDefault();
      if (!useWorkflowStore.getState().isSavingProject) saveShortcutRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSaveConfirm = useCallback(async () => {
    if (!projectNameInput.trim()) return;
    try {
      // «Nuevo proyecto»: recién acá se vacía el lienzo (cancelar no toca nada)
      // y el proyecto queda creado en el servidor con su nombre.
      if (saveMode === 'new') newProject();
      await saveProjectAsNew(projectNameInput.trim(), projectDescInput.trim());
      setShowSaveModal(false);
      setSaveSuccess(true);
      toast.success(`"${projectNameInput.trim()}" creado`, { duration: 2000, position: 'bottom-right' });
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      toast.error('Error al guardar: ' + error.message, { position: 'bottom-right' });
    }
  }, [projectNameInput, projectDescInput, saveProjectAsNew, saveMode, newProject]);

  const handleImportClick = () => fileInputRef.current?.click();

  const handleStartWorkflow = useCallback(async () => {
    if (isRunningWorkflow) {
      cancelWorkflow();
      toast.info('Flujo cancelado', { duration: 2000, position: 'bottom-right' });
      return;
    }
    const result = await startWorkflow();
    if (result?.cancelled) return;   // el usuario eligió no mandar nada
    if (result?.error) {
      toast.error(result.error, { duration: 3000, position: 'bottom-right' });
    } else {
      toast.info(`Ejecutando flujo: ${result?.total || 0} nodos`, { duration: 2000, position: 'bottom-right' });
    }
  }, [isRunningWorkflow, startWorkflow, cancelWorkflow]);

  // Envío UI-2 · "Guardar como…" (antes sólo existía al guardar un proyecto nuevo)
  const handleSaveAsClick = useCallback(() => {
    setSaveMode('save');
    setProjectNameInput(currentProjectId ? `${projectName} (copia)` : projectName);
    setProjectDescInput('');
    setShowSaveModal(true);
  }, [currentProjectId, projectName]);

  const handleNewProjectClick = useCallback(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setSaveMode('new');
    setProjectNameInput(`Proyecto ${pad(d.getDate())}-${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setProjectDescInput('');
    setShowSaveModal(true);
  }, []);

  const handleClearCanvas = useCallback(() => {
    if (nodes.length === 0) return;
    if (window.confirm('¿Borrar el escenario actual y empezar uno nuevo? Los cambios no guardados se perderán.')) {
      clearCanvas();
      toast.success('Escenario limpiado', { duration: 1500, position: 'bottom-right' });
    }
  }, [nodes.length, clearCanvas]);

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const success = importProject(e.target.result);
        if (success) {
          toast.success('Proyecto importado', { position: 'bottom-right' });
        } else {
          toast.error('Error al importar. Verifica el archivo JSON.', { position: 'bottom-right' });
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  };


  // ── Envío UI-2 · barra única ─────────────────────────────────────────────
  // Mismos handlers y mismos data-testid que las dos barras de siempre: sólo
  // cambia dónde está cada botón. Canvas / Edición = timelineStore.isOpen.
  const runV2 = (fn) => () => { setV2Menu(null); fn(); };
  const renderV2Bar = () => (
    <nav className="top-nav sml-v2-bar" data-testid="top-nav">
      <div className="v2-bar-left">
        <div
          className={`nav-logo ${activeCount > 0 ? 'is-rendering' : ''}`}
          data-testid="nav-logo"
          data-active-jobs={activeCount}
          title={activeCount > 0 ? `${activeCount} render${activeCount > 1 ? 's' : ''} en proceso` : 'Studio MediaLabs'}
        >
          <img src="/img/studio_media_lab_logo.png" alt="Studio MediaLabs" className="nav-logo-image" />
        </div>

        <div className="v2-menu-wrap">
          <button
            type="button"
            className={`v2-project-btn ${v2Menu === 'project' ? 'is-open' : ''}`}
            onClick={() => setV2Menu((m) => (m === 'project' ? null : 'project'))}
            aria-haspopup="menu"
            aria-expanded={v2Menu === 'project'}
            data-testid="current-project-indicator"
            title="Proyecto: abrir, guardar, importar…"
          >
            <span className="nav-project-name">{projectName}</span>
            {hasUnsavedChanges && <span className="nav-unsaved-dot" title="Cambios sin guardar" />}
            <span className="v2-project-count">{nodes.length} nodos</span>
            <ChevronDown size={13} />
          </button>
          {v2Menu === 'project' && (
            <div className="v2-menu" role="menu" data-testid="v2-project-menu">
              {/* Envío PJ-2 · lo que usa este proyecto: marca, reparto y plantilla */}
              {currentProjectId && (
                <>
                  <ProjectPersonalization projectId={currentProjectId}
                    onPick={(row) => runV2(() => openTargetRef.current?.(row.target, { tab: row.tab }))()} />
                  <div className="v2-menu-sep" />
                </>
              )}
              <div className="v2-menu-head">Proyecto</div>
              <button type="button" role="menuitem" onClick={runV2(() => setShowProjectsPanel(true))} data-testid="projects-btn">
                <FolderOpen size={14} /> Ver todos los proyectos…
              </button>
              <button type="button" role="menuitem" onClick={runV2(handleNewProjectClick)} data-testid="new-project-icon-btn">
                <Plus size={14} /> Nuevo proyecto
              </button>
              <button type="button" role="menuitem" onClick={runV2(handleSaveClick)} disabled={isSavingProject} data-testid="save-project-icon-btn">
                <Save size={14} /> Guardar <kbd>Ctrl S</kbd>
              </button>
              <button type="button" role="menuitem" onClick={runV2(handleSaveAsClick)} data-testid="save-as-btn">
                <Save size={14} /> Guardar como…
              </button>
              <div className="v2-menu-sep" />
              <div className="v2-menu-head">Archivo</div>
              <button type="button" role="menuitem" onClick={runV2(handleImportClick)} data-testid="import-json-btn">
                <Upload size={14} /> Importar JSON…
              </button>
              <button type="button" role="menuitem" onClick={runV2(exportProject)} data-testid="export-json-btn">
                <Download size={14} /> Exportar JSON
              </button>
              <div className="v2-menu-sep" />
              <button type="button" role="menuitem" className="is-danger" onClick={runV2(handleClearCanvas)} disabled={nodes.length === 0} data-testid="clear-canvas-btn">
                <Trash2 size={14} /> Limpiar escenario…
              </button>
            </div>
          )}
        </div>

        {hasUnsavedChanges && currentProjectId && (
          <button
            type="button"
            className={`v2-save-quick ${saveSuccess ? 'success' : ''}`}
            onClick={handleSaveClick}
            disabled={isSavingProject}
            title="Guardar proyecto (Ctrl+S)"
            data-testid="save-quick-btn"
          >
            {isSavingProject ? <Loader2 size={13} className="spin-icon" /> : <Save size={13} />}
            Guardar
          </button>
        )}
      </div>

      <div className="v2-mode" role="tablist" aria-label="Escenario">
        <button
          type="button"
          role="tab"
          aria-selected={!timelineOpen}
          className={`v2-mode-tab is-canvas ${!timelineOpen ? 'active' : ''}`}
          onClick={() => useTimelineStore.getState().closeEditor()}
          data-testid="mode-canvas-tab"
        >
          <LayoutGrid size={15} /> Canvas
          {timelineOpen && newNodesWhileEditing > 0 && (
            <span className="v2-mode-badge" title="Nodos nuevos en el canvas" data-testid="mode-canvas-badge">
              {newNodesWhileEditing} {newNodesWhileEditing === 1 ? 'nuevo' : 'nuevos'}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={timelineOpen}
          className={`v2-mode-tab is-edit ${timelineOpen ? 'active' : ''}`}
          onClick={() => useTimelineStore.getState().openEditor()}
          data-testid="timeline-toggle-btn"
        >
          <Clapperboard size={15} /> Edición
        </button>
      </div>

      <div className="v2-bar-right">
        {/* I-51 · con Clerk, el historial vive en el menú del usuario. */}
        {!clerkEnabled && (
        <button
          className={`workflow-icon-btn ${activeCount > 0 ? 'has-active' : ''}`}
          onClick={() => setShowExecutionHistory(true)}
          title={activeCount > 0
            ? `${processingList.length} procesando · ${queuedList.length} en cola`
            : 'Historial de ejecuciones'}
          data-testid="execution-history-btn"
        >
          <Activity size={15} />
          {activeCount > 0 && (
            <span className="exec-active-badge" data-testid="exec-active-badge">{activeCount}</span>
          )}
        </button>
        )}

        {!timelineOpen && showRunButton && (
          <button
            className={`workflow-run-btn workflow-run-btn-hero ${isRunningWorkflow ? 'running' : ''} ${spotlightRun ? 'spotlight' : ''}`}
            onClick={handleStartWorkflow}
            title={isRunningWorkflow ? 'Cancelar flujo' : 'Ejecutar flujo completo desde el inicio'}
            data-testid="run-workflow-btn"
          >
            {isRunningWorkflow ? (
              <>
                <StopCircle size={16} />
                <span className="workflow-progress-text">{workflowCurrentIndex + 1}/{workflowQueue.length}</span>
              </>
            ) : (
              <>
                <PlayCircle size={16} />
                <span>Ejecutar flujo</span>
                {inFlightCount > 0 && (
                  <span className="workflow-inflight-hint" data-testid="workflow-inflight-count">· {inFlightCount} generando</span>
                )}
              </>
            )}
          </button>
        )}

        {/* AP-3 · «Exportar» se mudó a la barra del timeline (vista Edición). */}

        <div className="utility-separator" />

        {/* I-48 / I-51 · la Galería ya no está en la barra: en Canvas va en el
            panel de funciones (abajo a la izquierda) y en Edición en las
            herramientas del preview. */}

        <CreditsBadge onClick={() => setShowCreditsPanel(true)} />

        <div className="v2-menu-wrap">
          <button
            type="button"
            className={`v2-icon-btn v2-round ${v2Menu === 'help' ? 'is-open' : ''}`}
            onClick={() => { setV2Menu((m) => (m === 'help' ? null : 'help')); setShowShortcuts(false); }}
            aria-haspopup="menu"
            aria-expanded={v2Menu === 'help'}
            aria-label="Ayuda"
            title="Ayuda"
            data-testid="help-menu-btn"
            style={{ position: 'relative' }}
          >
            ?
            {learningUnseen && <span className="sml-help-dot" aria-hidden="true" />}
          </button>
          {v2Menu === 'help' && (
            <div className="v2-menu is-right" role="menu" data-testid="help-menu">
              {learningAvailable && (
                <button type="button" role="menuitem" onClick={runV2(() => openLearning())} data-testid="help-menu-learning">
                  <MonitorPlay size={14} /> Videos instructivos
                  {learningUnseen && <span className="sml-help-new">NUEVO</span>}
                </button>
              )}
              {tourEnabled && (
                <>
                  <button type="button" role="menuitem" onClick={runV2(() => openTour())} data-testid="help-menu-tour"><Compass size={14} /> Recorrido guiado</button>
                  <button type="button" role="menuitem" onClick={runV2(() => openTour('demo'))} data-testid="help-menu-demo"><PlayCircle size={14} /> Ver la demo de LMX</button>
                </>
              )}
              <button type="button" role="menuitem" aria-expanded={showShortcuts} onClick={() => setShowShortcuts((v) => !v)} data-testid="help-menu-shortcuts">
                <Keyboard size={14} /> Atajos de teclado
              </button>
              {showShortcuts && (
                <div className="tour-menu-shortcuts" data-testid="help-menu-shortcut-list">
                  <div><kbd>Ctrl K</kbd> ir al nodo</div>
                  <div><kbd>Esc</kbd> plegar LMX</div>
                  <div><kbd>Shift ?</kbd> recorrido</div>
                </div>
              )}
              {/* I-49 · sin «Asistente de ayuda»: LMX es la guía de la plataforma. */}
              <div className="v2-menu-sep" />
              <button type="button" role="menuitem" onClick={runV2(() => openFeedback('problem'))} data-testid="help-menu-report">
                <AlertTriangle size={14} /> Reportar un problema
              </button>
              <button type="button" role="menuitem" onClick={runV2(() => openFeedback('idea'))} data-testid="help-menu-idea">
                <Lightbulb size={14} /> Sugerir una mejora
              </button>
            </div>
          )}
        </div>

        {!clerkEnabled && (
        <div className="v2-menu-wrap">
          <button
            type="button"
            className={`v2-icon-btn ${v2Menu === 'settings' ? 'is-open' : ''}`}
            onClick={() => setV2Menu((m) => (m === 'settings' ? null : 'settings'))}
            aria-haspopup="menu"
            aria-expanded={v2Menu === 'settings'}
            aria-label="Configuración"
            title="Configuración"
            data-testid="settings-menu-btn"
            data-mcp-access={mcpAccess ? '1' : '0'}
          >
            <Settings size={16} />
          </button>
          {v2Menu === 'settings' && (
            <div className="v2-menu is-right" role="menu" data-testid="settings-menu">
              <div className="v2-menu-head">Configuración</div>
              <label className="v2-menu-check">
                <input type="checkbox" checked={soundOn} onChange={handleToggleSound} data-testid="toggle-exec-sound" />
                <span>Sonido al terminar</span>
              </label>
              <Link to="/settings?tab=social" className="v2-menu-link" onClick={() => setV2Menu(null)} data-testid="nav-social-settings">
                <Share2 size={14} /> Redes sociales
              </Link>
              {mcpAccess && (
                <button type="button" role="menuitem" onClick={runV2(() => setShowMcpPanel(true))} data-testid="mcp-panel-trigger">
                  <Plug size={14} /> Conexiones MCP
                </button>
              )}
              {isAdmin && (
                <>
                  <div className="v2-menu-sep" />
                  <button type="button" role="menuitem" onClick={runV2(() => setShowAdminPanel(true))} data-testid="admin-panel-trigger">
                    <Shield size={14} /> Administración
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {clerkEnabled && (
          <UserMenu
            soundOn={soundOn}
            onToggleSound={handleToggleSound}
            mcpAccess={mcpAccess}
            isAdmin={isAdmin}
            activeCount={activeCount}
            onOpenCredits={() => setShowCreditsPanel(true)}
            onOpenHistory={() => setShowExecutionHistory(true)}
            onOpenMcp={() => setShowMcpPanel(true)}
            onOpenAdmin={() => setShowAdminPanel(true)}
          />
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileImport} style={{ display: 'none' }} data-testid="import-json-input" />
    </nav>
  );

  return (
    <>
      <LowBalanceBanner />
      {LAYOUT_V2 ? renderV2Bar() : (
      <nav className="top-nav top-nav-tiered" data-testid="top-nav">
        {/* ── TIER 1 · Utility bar ─────────────────────
           Global navigation: workspace-wide actions the user
           reaches for occasionally. Neutral tone, subtle background,
           the only accented item is "Crear con IA" (primary CTA). */}
        <div className="top-nav-tier tier-utility">
          <div className="tier-utility-left">
            <div
              className={`nav-logo ${activeCount > 0 ? 'is-rendering' : ''}`}
              data-testid="nav-logo"
              data-active-jobs={activeCount}
              title={activeCount > 0 ? `${activeCount} render${activeCount > 1 ? 's' : ''} en proceso` : 'Studio MediaLabs'}
            >
              <img
                src="/img/studio_media_lab_logo.png"
                alt="Studio MediaLabs"
                className="nav-logo-image"
              />
            </div>

            <div className="utility-group">
              <button
                className="utility-btn"
                onClick={() => setShowProjectsPanel(true)}
                title="Gestionar proyectos"
                data-testid="projects-btn"
              >
                <FolderOpen size={14} />
                <span>Proyectos</span>
              </button>
              <button
                className="utility-btn"
                onClick={() => setShowGallery(true)}
                title="Galería de medios"
                data-testid="gallery-btn"
              >
                <Image size={14} />
                <span>Galería</span>
              </button>
              <TimelineToggleButton />
              <CanvasBackgroundSelector />
            </div>

            {isAdmin && (
              <>
                <div className="utility-separator" />
                <button
                  className="utility-btn utility-btn-admin"
                  onClick={() => setShowAdminPanel(true)}
                  title="Panel de administración"
                  data-testid="admin-panel-trigger"
                >
                  <Shield size={14} />
                  <span>Administración</span>
                </button>
              </>
            )}
            {mcpAccess && (
              <button
                className="utility-btn"
                onClick={() => setShowMcpPanel(true)}
                title="Conexiones MCP (agentes)"
                data-testid="mcp-panel-trigger"
              >
                <Plug size={14} />
                <span>MCP</span>
              </button>
            )}
          </div>

          <div className="tier-utility-right">
            <button
              type="button"
              className={`help-toggle ${showHelpAgent ? 'is-on' : ''}`}
              onClick={() => setShowHelpAgent((v) => !v)}
              title={showHelpAgent ? 'Apagar asistente' : 'Encender asistente'}
              data-testid="help-agent-trigger"
              aria-pressed={showHelpAgent}
            >
              <HelpCircle size={14} />
              <span className="help-toggle-label">Ayuda</span>
              <span className="help-toggle-switch" aria-hidden="true">
                <span className="help-toggle-thumb" />
              </span>
            </button>

            {/* E6f · Ayuda y recorrido: el tour se puede volver a ver siempre */}
            {tourEnabled && (
            <div className="tour-menu-wrap">
              <button
                type="button"
                className="tour-menu-btn"
                onClick={() => { setShowTourMenu((v) => !v); setShowShortcuts(false); }}
                title="Ayuda y recorrido"
                aria-label="Ayuda y recorrido"
                data-testid="help-menu-btn"
              >
                ?
              </button>
              {showTourMenu && (
                <div className="tour-menu" role="menu" data-testid="help-menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setShowTourMenu(false); openTour(); }}
                    data-testid="help-menu-tour"
                  >
                    Recorrido guiado
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setShowTourMenu(false); openTour('demo'); }}
                    data-testid="help-menu-demo"
                  >
                    Ver la demo de LMX
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    aria-expanded={showShortcuts}
                    onClick={() => setShowShortcuts((v) => !v)}
                    data-testid="help-menu-shortcuts"
                  >
                    Atajos de teclado
                  </button>
                  {showShortcuts && (
                    <div className="tour-menu-shortcuts" data-testid="help-menu-shortcut-list">
                      <div><kbd>Ctrl K</kbd> ir al nodo</div>
                      <div><kbd>Esc</kbd> plegar LMX</div>
                      <div><kbd>Shift ?</kbd> recorrido</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            <div className="utility-separator" />

            <CreditsBadge onClick={() => setShowCreditsPanel(true)} />

            {clerkEnabled && (
              <div className="utility-user-slot">
                <UserProfile />
              </div>
            )}
          </div>
        </div>

        {/* ── TIER 2 · Workflow bar ────────────────────
           Actions scoped to the current canvas: project ident,
           node count, run/save/import/export, execution monitor. */}
        <div className="top-nav-tier tier-workflow">
          <div className="tier-workflow-left">
            <div className="nav-current-project" data-testid="current-project-indicator">
              <span className="nav-project-name">{projectName}</span>
              {hasUnsavedChanges && <span className="nav-unsaved-dot" title="Cambios sin guardar" />}
            </div>
            <div className="workflow-stat">
              <span className="workflow-stat-value">{nodes.length}</span>
              <span className="workflow-stat-label">nodos</span>
            </div>
          </div>

          <div className="tier-workflow-right">
            <button
              className={`workflow-icon-btn ${activeCount > 0 ? 'has-active' : ''}`}
              onClick={() => setShowExecutionHistory(true)}
              title={activeCount > 0
                ? `${processingList.length} procesando · ${queuedList.length} en cola`
                : 'Historial de ejecuciones'}
              data-testid="execution-history-btn"
            >
              <Activity size={15} />
              {activeCount > 0 && (
                <span className="exec-active-badge" data-testid="exec-active-badge">
                  {activeCount}
                </span>
              )}
            </button>

            <div className="utility-separator" />

            <button className="workflow-icon-btn" onClick={handleNewProjectClick} title="Nuevo proyecto" data-testid="new-project-icon-btn">
              <Plus size={15} />
            </button>
            <button
              className={`workflow-icon-btn ${saveSuccess ? 'success' : ''} ${hasUnsavedChanges && currentProjectId ? 'has-changes' : ''}`}
              onClick={handleSaveClick}
              title={currentProjectId ? 'Guardar proyecto (Ctrl+S)' : 'Guardar como nuevo'}
              disabled={isSavingProject}
              data-testid="save-project-icon-btn"
            >
              {isSavingProject
                ? <Loader2 size={15} className="spin-icon" />
                : saveSuccess ? <Check size={15} /> : <Save size={15} />}
            </button>
            <button className="workflow-icon-btn" onClick={handleImportClick} title="Importar JSON">
              <Upload size={15} />
            </button>
            <button className="workflow-icon-btn" onClick={exportProject} title="Exportar JSON">
              <Download size={15} />
            </button>

            <div className="utility-separator" />

            <button className="workflow-icon-btn danger" onClick={() => {
              if (nodes.length === 0) return;
              if (window.confirm('¿Borrar el escenario actual y empezar uno nuevo? Los cambios no guardados se perderán.')) {
                clearCanvas();
                toast.success('Escenario limpiado', { duration: 1500, position: 'bottom-right' });
              }
            }} title="Nuevo escenario (limpiar canvas)">
              <Trash2 size={15} />
            </button>

            <div className="nav-settings-dropdown">
              <button className="workflow-icon-btn" title="Configuración">
                <Settings size={15} />
              </button>
              <div className="nav-dropdown-content">
                <label className="nav-dropdown-item">
                  <input type="checkbox" checked={showFooter} onChange={toggleFooter} />
                  <span>Mostrar Footer</span>
                </label>
                <label className="nav-dropdown-item">
                  <input type="checkbox" checked={showBadge} onChange={toggleBadge} />
                  <span>Mostrar Badge</span>
                </label>
                <label className="nav-dropdown-item">
                  <input
                    type="checkbox"
                    checked={soundOn}
                    onChange={handleToggleSound}
                    data-testid="toggle-exec-sound"
                  />
                  <span>Sonido al terminar</span>
                </label>
                <Link
                  to="/settings?tab=social"
                  className="nav-dropdown-item nav-dropdown-link"
                  data-testid="nav-social-settings"
                >
                  <Share2 size={13} />
                  <span>Redes sociales</span>
                </Link>
              </div>
            </div>

            <div className="utility-separator" />

            <button
              className={`workflow-run-btn workflow-run-btn-hero ${isRunningWorkflow ? 'running' : ''} ${spotlightRun ? 'spotlight' : ''}`}
              onClick={handleStartWorkflow}
              title={isRunningWorkflow ? 'Cancelar flujo' : 'Ejecutar flujo completo desde el inicio'}
              data-testid="run-workflow-btn"
            >
              {isRunningWorkflow ? (
                <>
                  <StopCircle size={16} />
                  <span className="workflow-progress-text">
                    {workflowCurrentIndex + 1}/{workflowQueue.length}
                  </span>
                </>
              ) : (
                <>
                  <PlayCircle size={16} />
                  <span>Ejecutar flujo completo</span>
                  {inFlightCount > 0 && (
                    <span className="workflow-inflight-hint" data-testid="workflow-inflight-count">
                      · {inFlightCount} generando
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileImport} style={{ display: 'none' }} />
      </nav>
      )}

      {/* Save As Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-content save-modal" onClick={e => e.stopPropagation()} data-testid="save-modal">
            <div className="modal-header">
              <h3>{saveMode === 'new' ? 'Nuevo proyecto' : 'Guardar Proyecto'}</h3>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nombre del proyecto</label>
                <input
                  type="text"
                  value={projectNameInput}
                  onChange={e => setProjectNameInput(e.target.value)}
                  placeholder="Mi proyecto"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleSaveConfirm()}
                  data-testid="project-name-input"
                />
              </div>
              <div className="form-group">
                <label>Descripción (opcional)</label>
                <textarea
                  value={projectDescInput}
                  onChange={e => setProjectDescInput(e.target.value)}
                  placeholder="Describe tu proyecto..."
                  rows={3}
                  data-testid="project-desc-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn secondary" onClick={() => setShowSaveModal(false)}>
                Cancelar
              </button>
              <button
                className="modal-btn primary"
                onClick={handleSaveConfirm}
                disabled={isSavingProject || !projectNameInput.trim()}
                data-testid="save-confirm-btn"
              >
                {isSavingProject ? <Loader2 size={14} className="spin-icon" /> : <Save size={14} />}
                {saveMode === 'new' ? 'Crear proyecto' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panels */}
      <ProjectsPanel isOpen={showProjectsPanel} onClose={() => setShowProjectsPanel(false)} />
      <ExecutionHistory isOpen={showExecutionHistory} onClose={() => setShowExecutionHistory(false)} />
      <CreditsPanel isOpen={showCreditsPanel} onClose={() => setShowCreditsPanel(false)} />
      <BuyCreditsModal isOpen={showBuyModal} onClose={closeBuyModal} />
      {showGallery && <GalleryPanel onClose={() => setShowGallery(false)} />}
      {library && <LibraryWindow initialTab={library.tab} onClose={() => setLibrary(null)} />}
      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
      {showMcpPanel && <McpConnectionsPanel onClose={() => setShowMcpPanel(false)} />}
      {/* I-49 · el widget de ayuda por voz no se carga en la vista nueva. */}
      {!LAYOUT_V2 && <HelpAgentWidget open={showHelpAgent} onClose={() => setShowHelpAgent(false)} />}
    </>
  );
});

TopNavBar.displayName = 'TopNavBar';
export default TopNavBar;
